import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { SketchPicker } from 'react-color'
import { Popover, Stack, Button, IconButton } from '@mui/material'
import { useTheme } from '@mui/styles'
import {
  ColorLens as ColorLensIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material'

const ScaleViewMaterialCard = ({
  isCustomizable,
  containerWidth,
  buttonData,
  dragAnchorRef,
  updateColor,
  ...restProps
}) => {
  const theme = useTheme()
  const [anchorElement, setAnchorElement] = useState(false)

  const sharedStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'black',
    fontSize: 16,
    fontWeight: 500,
    border: 1,
    borderColor: 'grey.400',
    borderRadius: 2,
    height: 100,
    width: containerWidth ? (containerWidth - 40) / 4 : null,
    backgroundColor: buttonData?.color || 'colorPicker.white',
    transitionDuration: '.1s',
    '&:hover': {
      boxShadow:
        '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)',
      backgroundColor: buttonData?.color || 'colorPicker.white',
    },
    cursor: 'pointer',
  }

  const iconButtonStyles = {
    padding: 1,
    alignSelf: 'flex-start',
    color: 'primary.actionMain',
  }

  return !isCustomizable ? (
    <Button
      sx={{
        ...sharedStyles,
      }}
      fullWidth
      startIcon={buttonData?.id === 'back' && <ArrowBackIcon />}
      {...restProps}
      data-testid={buttonData?.id === 'back' ? 'back-button' : 'material-commodity-button'}
    >
      {buttonData?.id === 'back' ? 'BACK' : buttonData?.materialName || buttonData?.name}
    </Button>
  ) : (
    <Stack
      direction="row"
      justifyContent="space-between"
      sx={{
        ...sharedStyles,
      }}
      {...restProps}
    >
      {buttonData?.id !== 'back' && (
        <>
          <IconButton
            size="small"
            sx={{
              ...iconButtonStyles,
            }}
            onClick={event => {
              event.stopPropagation()
              setAnchorElement(event.target)
            }}
          >
            <ColorLensIcon />
          </IconButton>
          <Popover
            open={!!anchorElement}
            anchorEl={anchorElement}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
            onClick={event => event.stopPropagation()}
            onClose={() => setAnchorElement(null)}
          >
            <SketchPicker
              disableAlpha
              color={buttonData?.color || theme.palette.colorPicker.white}
              onChange={event => {
                updateColor(buttonData?.id, event.hex)
              }}
              presetColors={[...Object.values(theme.palette.colorPicker)]}
            />
          </Popover>
        </>
      )}
      <Stack width="100%" direction="row" justifyContent="center">
        {buttonData?.id === 'back' ? (
          <>
            <ArrowBackIcon sx={{ marginRight: 0.5, paddingRight: 0.5 }} />
            BACK
          </>
        ) : (
          buttonData?.name
        )}
      </Stack>
      {buttonData?.id !== 'back' && (
        <IconButton
          ref={dragAnchorRef}
          size="small"
          sx={{
            ...iconButtonStyles,
            cursor: 'grab',
            '&:hover': {
              backgroundColor: 'unset',
            },
          }}
          onClick={event => event.stopPropagation()}
        >
          <DragIndicatorIcon />
        </IconButton>
      )}
    </Stack>
  )
}

ScaleViewMaterialCard.propTypes = {
  isCustomizable: PropTypes.bool,
  containerWidth: PropTypes.number,
  buttonData: PropTypes.object.isRequired,
  dragAnchorRef: PropTypes.func,
  updateColor: PropTypes.func,
}

ScaleViewMaterialCard.defaultProps = {
  isCustomizable: false,
  containerWidth: 0,
  dragAnchorRef: null,
  updateColor: () => {},
}

export default ScaleViewMaterialCard
